import { useQuery } from 'react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { utils as XLSXUtils, writeFile } from 'xlsx';
import { fetchRendicontazioni } from '../api/rendicontazioni';
import { Rendicontazione } from '../interface/rendicontazione';
import { useAuth } from '../components/auth/AuthProvider';
import { Navbar } from '../components/Navbar';
import { ErrorContent } from '../components/ErrorContent';
import { provinces } from '../data/provinces';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

export const Home = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [eventoChiuso, setEventoChiuso] = useState<string>('');
  const [provincia, setProvincia] = useState<string>('');

  const {
    data: rendicontazioni,
    isLoading,
    error,
  } = useQuery<Rendicontazione[]>(
    ['rendicontazioni', eventoChiuso, provincia],
    async () => {
      const params = new URLSearchParams();
      if (eventoChiuso) {
        params.append('sent', eventoChiuso);
      }
      if (provincia) {
        params.append('provincia', provincia);
      }
      return fetchRendicontazioni(token, params);
    },
    {
      cacheTime: eventoChiuso || provincia ? 0 : 5 * 60 * 1000, // 0 if filters are active, 5 minutes otherwise
    },
  );

  const handleExportExcel = () => {
    if (!rendicontazioni) return;

    const worksheet = XLSXUtils.json_to_sheet(rendicontazioni);
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, worksheet, 'Rendicontazioni');
    writeFile(workbook, 'rendicontazioni.xlsx');
  };

  if (error) {
    return (
      <>
        <Navbar onLogout={logout} />
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h1">
              Rendicontazioni
            </Typography>
          </Box>
          <ErrorContent />
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar onLogout={logout} />
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h1">
            Rendicontazioni
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="evento-chiuso-label">Evento chiuso</InputLabel>
              <Select
                labelId="evento-chiuso-label"
                value={eventoChiuso}
                label="Evento chiuso"
                onChange={(e) => setEventoChiuso(e.target.value)}
              >
                <MenuItem value="">Tutti gli eventi</MenuItem>
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Sì</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="provincia-label">Provincia</InputLabel>
              <Select
                labelId="provincia-label"
                value={provincia}
                label="Provincia"
                onChange={(e) => setProvincia(e.target.value)}
              >
                <MenuItem value="">Tutte le province</MenuItem>
                {provinces.map((p) => (
                  <MenuItem key={p.sigla} value={p.nome}>
                    {p.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={!rendicontazioni?.length}
            >
              Esporta in Excel
            </Button>
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '60px' }}>ID</TableCell>
                  <TableCell sx={{ width: '100px' }}>ID Manifestazione</TableCell>
                  <TableCell sx={{ width: '60px' }}>ID Evento</TableCell>
                  <TableCell sx={{ width: '150px' }}>Username Volontario</TableCell>
                  <TableCell sx={{ width: '100px' }}>Provincia</TableCell>
                  <TableCell sx={{ width: '120px' }}>Comune</TableCell>
                  <TableCell sx={{ width: '150px' }}>Nome piazza</TableCell>
                  <TableCell sx={{ width: '150px' }}>Nome responsabile</TableCell>
                  <TableCell sx={{ width: '100px' }}>Evento chiuso</TableCell>
                  <TableCell sx={{ width: '120px' }}>Quantità ricevuti</TableCell>
                  <TableCell sx={{ width: '150px' }}>Quantità distribuiti digitali</TableCell>
                  <TableCell sx={{ width: '200px' }}>Quantità distribuiti extra Donandi</TableCell>
                  <TableCell sx={{ width: '120px' }}>Totale distribuiti</TableCell>
                  <TableCell sx={{ width: '200px' }}>Contributi donazioni extra Donandi</TableCell>
                  <TableCell sx={{ width: '200px' }}>Contributi acquisti extra Donandi</TableCell>
                  <TableCell sx={{ width: '120px' }}>Contributi raccolti</TableCell>
                  <TableCell sx={{ width: '150px' }}>Quantità non utilizzabili</TableCell>
                  <TableCell sx={{ width: '100px' }}>Rimanenze</TableCell>
                  <TableCell sx={{ width: '180px' }}>Ricevute cartacee utilizzate</TableCell>
                  <TableCell sx={{ width: '180px' }}>Ricevute digitali utilizzate</TableCell>
                  <TableCell sx={{ width: '180px' }}>Richiesta per anno successivo</TableCell>
                  <TableCell sx={{ width: '200px' }}>Quantità prese da altre piazze</TableCell>
                  <TableCell sx={{ width: '200px' }}>Quantità date ad altre piazze</TableCell>
                  <TableCell sx={{ width: '200px' }}>Commenti</TableCell>
                  <TableCell sx={{ width: '150px' }}>Chiusura con riserva</TableCell>
                  <TableCell sx={{ width: '200px' }}>Note chiusura</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rendicontazioni?.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => navigate(`/event/${row.id}`)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.id_manifestazione}</TableCell>
                    <TableCell>{row.id_evento}</TableCell>
                    <TableCell>{row.username_volontario}</TableCell>
                    <TableCell>{row.provincia}</TableCell>
                    <TableCell>{row.comune}</TableCell>
                    <TableCell>{row.nome_piazza || '-'}</TableCell>
                    <TableCell>{row.nome_responsabile || '-'}</TableCell>
                    <TableCell>{row.sent ? 'Sì' : 'No'}</TableCell>
                    <TableCell>{row.qt_ricevuti}</TableCell>
                    <TableCell>{row.qt_ricevute_digitali}</TableCell>
                    <TableCell>{row.qt_distribuiti_non_registrati}</TableCell>
                    <TableCell>{row.qt_distribuiti}</TableCell>
                    <TableCell>€{Number(row.tot_donazioni_non_registrati).toFixed(2)}</TableCell>
                    <TableCell>€{Number(row.tot_distribuiti_non_registrati).toFixed(2)}</TableCell>
                    <TableCell>€{Number(row.tot_contributi).toFixed(2)}</TableCell>
                    <TableCell>{row.qt_non_utilizzabili}</TableCell>
                    <TableCell>{row.qt_rimanenze}</TableCell>
                    <TableCell>{row.qt_ricevute_cartacee}</TableCell>
                    <TableCell>{row.qt_ricevute_digitali}</TableCell>
                    <TableCell>{row.qt_richiesta_anno_successivo}</TableCell>
                    <TableCell>{row.qt_piazze_esterne || '-'}</TableCell>
                    <TableCell>{row.qt_date_piazze_esterne || '-'}</TableCell>
                    <TableCell>{row.commenti || '-'}</TableCell>
                    <TableCell>{row.chiusura_con_riserva ? 'Sì' : 'No'}</TableCell>
                    <TableCell>{row.note || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};
